import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { ApiError } from '../../api/index.ts';
import type { OwnerSummary, ShopOwnerRef, ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { actionError } from '../../components/actionError.ts';
import { QuietButton } from '../../components/QuietButton.tsx';

export interface ShopOwnersSectionProps {
  shop: ShopSummary;
  canRead: boolean;
  canManage: boolean;
  onMutated: () => void;
}

export function ShopOwnersSection({
  shop,
  canRead,
  canManage,
  onMutated,
}: Readonly<ShopOwnersSectionProps>): React.JSX.Element {
  const { api } = useAuth();
  const [allOwners, setAllOwners] = useState<OwnerSummary[] | null>(null);
  const [pickerError, setPickerError] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | ''>('');
  const [linkError, setLinkError] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<ShopOwnerRef | null>(null);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    api.owners
      .list()
      .then((res) => {
        if (!cancelled) setAllOwners(res.owners);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPickerError(actionError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, canRead, shop.id]);

  const link = async () => {
    if (selectedOwnerId === '') return;
    setLinkBusy(true);
    setLinkError('');
    try {
      await api.owners.linkShop(Number(selectedOwnerId), shop.id);
      setSelectedOwnerId('');
      onMutated();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'owner_shop_conflict') {
        setLinkError(e.message);
      } else {
        setLinkError(actionError(e));
      }
    } finally {
      setLinkBusy(false);
    }
  };

  const linkedIds = new Set(shop.owners.map((o) => o.id));
  const pickable = (allOwners ?? []).filter((o) => !linkedIds.has(o.id));
  const detailsById = new Map((allOwners ?? []).map((owner) => [owner.id, owner]));
  const canLink = canRead && canManage;

  return (
    <Paper sx={{ p: 2.5 }}>
      {canLink && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={linkBusy || allOwners === null}>
            <InputLabel id="shop-owner-add-label">Add owner</InputLabel>
            <Select<number | ''>
              labelId="shop-owner-add-label"
              label="Add owner"
              value={selectedOwnerId}
              onChange={(e) => setSelectedOwnerId(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ 'aria-label': 'shop-owner-add' }}
            >
              {pickable.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.first_name} {o.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button disabled={linkBusy || selectedOwnerId === ''} onClick={() => void link()}>
            Link
          </Button>
        </Box>
      )}

      {pickerError && (
        <Alert role="alert" severity="error" sx={{ mt: 1.5 }}>
          {pickerError}
        </Alert>
      )}
      {linkError && (
        <Alert role="alert" severity="error" sx={{ mt: 1.5, fontWeight: 700 }}>
          {linkError}
        </Alert>
      )}

      <TableContainer sx={{ mt: pickerError || linkError ? 2 : 0 }}>
        <Table size="small" aria-label="Linked owners">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {shop.owners.length === 0 && <EmptyRow colSpan={canManage ? 4 : 3} message="No owners linked." />}
            {shop.owners.map((owner) => {
              const details = detailsById.get(owner.id);
              return (
                <TableRow key={owner.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{`${owner.first_name} ${owner.last_name}`.trim()}</TableCell>
                  <TableCell>{canRead ? (details?.phone ?? '-') : '-'}</TableCell>
                  <TableCell>{canRead ? (details?.email ?? '-') : '-'}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <QuietButton size="small" onClick={() => setUnlinkTarget(owner)}>
                        Unlink
                      </QuietButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={unlinkTarget !== null}
        title="Unlink owner"
        body={unlinkTarget ? `Unlink ${unlinkTarget.first_name} ${unlinkTarget.last_name} from this shop?` : ''}
        confirmLabel="Unlink"
        confirmColor="primary"
        onConfirm={async () => {
          if (unlinkTarget) await api.owners.unlinkShop(unlinkTarget.id, shop.id);
        }}
        onClose={() => setUnlinkTarget(null)}
        onSuccess={onMutated}
      />
    </Paper>
  );
}

export default ShopOwnersSection;
